import React, { useEffect, useMemo } from "react";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  Container,
  Fade,
  Grid,
  Stack,
  Typography,
  useTheme,
  Skeleton,
  alpha,
} from "@mui/material";
import {
  TrendingUp,
  MoneyOff,
  AttachMoney,
  Add,
  Assessment,
  Category,
  Insights,
  Schedule,
  South,
  NorthEast,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import SurfaceCard from "../common/ui/SurfaceCard";
import PrimaryButton from "../common/ui/PrimaryButton";
import { 
  EXPENSES_ROUTE_PATH, 
  INCOMES_ROUTE_PATH, 
  MONTHLY_REPORTS_ROUTE_PATH, 
  CATEGORIES_ROUTE_PATH 
} from "../../utils/constants/AppConstants";

import { useExpensesInRange } from "../../hooks/expense";
import { useIncomesInRange } from "../../hooks/income";
import { useCurrencyFormatter } from "../../contexts/CurrencyContext";
import Expense from "../../models/finance/Expense";
import Income from "../../models/finance/Income";
import { useAuth } from "../../auth/AuthContext";
import { useError } from "../../error/useError";
import ErrorAlert from "../common/ErrorAlert";
import { useQueryClient } from "@tanstack/react-query";
import { getCategories } from "../../api/category";
import { CategoryType } from "../../models/finance/CategoryType";
import { useUserProfileContext } from "../../contexts/ProfileContext";

const LoggedInHomePageComponent: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { formatCurrencyNumber: formatCurrency } = useCurrencyFormatter();
  const { formatDate } = useUserProfileContext();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const { error, helpText, isLoading, clearError } = useError();
  const { startOfMonth, endOfMonth } = useMemo(() => {
    const now = new Date();
    return {
      startOfMonth: new Date(now.getFullYear(), now.getMonth(), 1),
      endOfMonth: new Date(now.getFullYear(), now.getMonth() + 1, 0),
    };
  }, []);
  const { userProfile } = useUserProfileContext();
  const { data: expensesData } = useExpensesInRange(startOfMonth, endOfMonth);
  const { data: incomesData } = useIncomesInRange(startOfMonth, endOfMonth);

  // Extract first name and limit length for welcome message
  const userName = userProfile?.name || 'User';
  const firstName = userName.split(' ')[0];
  const displayName = firstName.length > 15 ? firstName.substring(0, 15) + '...' : firstName;

  const totalExpenses = useMemo(() => (expensesData || []).reduce((sum, e) => sum + e.amount, 0), [expensesData]);
  const totalIncome = useMemo(() => (incomesData || []).reduce((sum, i) => sum + i.amount, 0), [incomesData]);
  const expensesList = useMemo<Expense[]>(() => expensesData || [], [expensesData]);
  const incomesList = useMemo<Income[]>(() => incomesData || [], [incomesData]);

  // Prefetch common data for snappier navigation
  useEffect(() => {
    if (!isAuthenticated) return;
    queryClient.prefetchQuery({
      queryKey: ["categories", CategoryType.Expense, false],
      queryFn: () => getCategories(CategoryType.Expense, false),
      staleTime: 60_000,
    });
    queryClient.prefetchQuery({
      queryKey: ["categories", CategoryType.Income, false],
      queryFn: () => getCategories(CategoryType.Income, false),
      staleTime: 60_000,
    });
  }, [isAuthenticated, queryClient]);

  const netBalance = totalIncome - totalExpenses;
  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const accentSoft = alpha(theme.palette.primary.main, 0.16);
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const elapsedDays = Math.max(1, now.getDate());
  const remainingDays = Math.max(0, daysInMonth - elapsedDays);
  const savingsRate = totalIncome > 0 ? (netBalance / totalIncome) * 100 : 0;
  const averageDailySpend = elapsedDays > 0 ? totalExpenses / elapsedDays : 0;
  const projectedMonthExpense = averageDailySpend * daysInMonth;
  const spendPaceRatio = totalIncome > 0 ? totalExpenses / totalIncome : 0;
  const spendPacePercent = Math.min(100, Math.max(0, spendPaceRatio * 100));

  const topExpenseCategories = useMemo(() => {
    const buckets = expensesList.reduce<Record<string, number>>((acc, item) => {
      const key = item.category || "Uncategorized";
      acc[key] = (acc[key] || 0) + item.amount;
      return acc;
    }, {});

    return Object.entries(buckets)
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 4);
  }, [expensesList]);

  const topIncomeCategories = useMemo(() => {
    const buckets = incomesList.reduce<Record<string, number>>((acc, item) => {
      const key = item.category || "Uncategorized";
      acc[key] = (acc[key] || 0) + item.amount;
      return acc;
    }, {});

    return Object.entries(buckets)
      .map(([name, amount]) => ({ name, amount }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 4);
  }, [incomesList]);

  const recentExpenses = useMemo<Expense[]>(() => {
    return [...expensesList]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 4);
  }, [expensesList]);

  const recentIncomes = useMemo<Income[]>(() => {
    return [...incomesList]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 4);
  }, [incomesList]);

  // Early return if user is not authenticated
  if (!isAuthenticated) {
    return null;
  }

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Fade in={true} timeout={1000}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Skeleton variant="circular" width={58} height={58} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={220} height={34} />
                <Skeleton variant="text" width={150} height={22} />
              </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[...Array(3)].map((_, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton variant="rectangular" height={132} sx={{ borderRadius: 3 }} />
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2}>
              {[...Array(3)].map((_, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 3 }} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Fade>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <ErrorAlert
          error={error}
          helpText={helpText}
          onClear={clearError}
          onRetry={() => queryClient.invalidateQueries()}
          title="Dashboard Error"
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1.5, sm: 2.5 } }}>
      <Fade in={true} timeout={1000}>
        <Box>
          <SurfaceCard
            sx={{
              p: { xs: 2.4, md: 3 },
              mb: 2.4,
              borderRadius: "18px",
              border: `1px solid ${accentSoft}`,
              background: `linear-gradient(150deg, ${theme.palette.background.paper}, ${theme.palette.background.default})`,
            }}
          >
            <Grid container spacing={2.2} alignItems="center">
              <Grid item xs={12} lg={8}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.1 }}>
                  <Avatar
                    sx={{
                      width: { xs: 52, sm: 60 },
                      height: { xs: 52, sm: 60 },
                      bgcolor: alpha(theme.palette.primary.main, 0.25),
                      color: theme.palette.primary.light,
                      fontWeight: 700,
                    }}
                  >
                    {userProfile?.name?.charAt(0).toUpperCase() || "U"}
                  </Avatar>

                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: '"Space Grotesk", "Manrope", sans-serif' }}>
                      Welcome back, {displayName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currentMonth} command center
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Chip icon={<Schedule />} label={`${remainingDays} days left in month`} sx={{ borderRadius: "10px" }} />
                  <Chip icon={<Insights />} label={`${expensesList.length + incomesList.length} transactions this month`} sx={{ borderRadius: "10px" }} />
                </Stack>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Alert
                  severity={netBalance >= 0 ? "success" : "warning"}
                  icon={false}
                  sx={{ borderRadius: "12px" }}
                >
                  {netBalance >= 0
                    ? "Positive cashflow this month. Consider routing surplus into savings goals."
                    : "Cashflow is negative this month. Prioritize categories with the highest spend growth."}
                </Alert>
              </Grid>
            </Grid>
          </SurfaceCard>

          <Grid container spacing={2} sx={{ mb: 2.4 }}>
            <Grid item xs={12} sm={6} lg={3}>
              <SurfaceCard sx={{ p: 2.2, borderRadius: "16px", height: "100%" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Expenses</Typography>
                  <MoneyOff sx={{ color: theme.palette.error.main }} />
                </Stack>
                <Typography variant="h5" sx={{ mt: 0.8, fontWeight: 700, color: theme.palette.error.light }}>
                  {formatCurrency(totalExpenses)}
                </Typography>
                <Typography variant="caption" color="text.secondary">Spent so far</Typography>
              </SurfaceCard>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <SurfaceCard sx={{ p: 2.2, borderRadius: "16px", height: "100%" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Income</Typography>
                  <AttachMoney sx={{ color: theme.palette.success.main }} />
                </Stack>
                <Typography variant="h5" sx={{ mt: 0.8, fontWeight: 700, color: theme.palette.success.light }}>
                  {formatCurrency(totalIncome)}
                </Typography>
                <Typography variant="caption" color="text.secondary">Received so far</Typography>
              </SurfaceCard>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <SurfaceCard sx={{ p: 2.2, borderRadius: "16px", height: "100%" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Net Balance</Typography>
                  <TrendingUp sx={{ color: theme.palette.primary.main }} />
                </Stack>
                <Typography
                  variant="h5"
                  sx={{
                    mt: 0.8,
                    fontWeight: 700,
                    color: netBalance >= 0 ? theme.palette.success.light : theme.palette.error.light,
                  }}
                >
                  {netBalance >= 0 ? "+" : ""}{formatCurrency(netBalance)}
                </Typography>
                <Typography variant="caption" color="text.secondary">Income - Expenses</Typography>
              </SurfaceCard>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <SurfaceCard sx={{ p: 2.2, borderRadius: "16px", height: "100%" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Savings Rate</Typography>
                  <TrendingUp sx={{ color: savingsRate >= 0 ? theme.palette.success.main : theme.palette.error.main }} />
                </Stack>
                <Typography
                  variant="h5"
                  sx={{
                    mt: 0.8,
                    fontWeight: 700,
                    color: savingsRate >= 0 ? theme.palette.success.light : theme.palette.error.light,
                  }}
                >
                  {savingsRate >= 0 ? "+" : ""}{savingsRate.toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">Of monthly income</Typography>
              </SurfaceCard>
            </Grid>
          </Grid>

          <Grid container spacing={2.2} sx={{ mb: 2.4 }}>
            <Grid item xs={12} lg={6}>
              <SurfaceCard sx={{ p: 2.2, borderRadius: "16px", height: "100%" }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Spending Pace</Typography>
                  <Chip
                    size="small"
                    label={spendPaceRatio > 0.9 ? "High pressure" : spendPaceRatio > 0.7 ? "Balanced" : "Healthy"}
                    sx={{
                      borderRadius: "8px",
                      ...(spendPaceRatio > 0.9
                        ? {
                            backgroundColor: alpha(theme.palette.error.main, 0.14),
                            color: theme.palette.error.light,
                          }
                        : {}),
                    }}
                  />
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Based on {elapsedDays} elapsed days, your projected expenses for {currentMonth} are {formatCurrency(projectedMonthExpense)}.
                </Typography>

                <Stack spacing={1.3}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.45 }}>
                      <Typography variant="caption" color="text.secondary">Expense vs Income</Typography>
                      <Typography variant="caption" color="text.secondary">{Math.min(999, spendPaceRatio * 100).toFixed(0)}%</Typography>
                    </Stack>
                    <Box
                      sx={{
                        position: "relative",
                        height: 8,
                        borderRadius: 999,
                        overflow: "hidden",
                        background: `linear-gradient(90deg, ${theme.palette.success.dark} 0%, ${theme.palette.primary.main} 62%, ${theme.palette.error.main} 100%)`,
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          bottom: 0,
                          left: `${spendPacePercent}%`,
                          backgroundColor: alpha(theme.palette.background.paper, 0.78),
                        }}
                      />
                    </Box>
                  </Box>

                  <Grid container spacing={1.1}>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ p: 1.2, borderRadius: "10px", backgroundColor: alpha(theme.palette.error.main, 0.08), border: `1px solid ${alpha(theme.palette.error.main, 0.2)}` }}>
                        <Typography variant="caption" color="text.secondary">Avg daily spend</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>{formatCurrency(averageDailySpend)}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ p: 1.2, borderRadius: "10px", backgroundColor: alpha(theme.palette.primary.main, 0.08), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                        <Typography variant="caption" color="text.secondary">Projected total</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>{formatCurrency(projectedMonthExpense)}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ p: 1.2, borderRadius: "10px", backgroundColor: alpha(theme.palette.success.main, 0.08), border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                        <Typography variant="caption" color="text.secondary">Days remaining</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>{remainingDays} days</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Stack>
              </SurfaceCard>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <SurfaceCard sx={{ p: 2.2, borderRadius: "16px", height: "100%" }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Top Spending Categories</Typography>
                  <South sx={{ color: theme.palette.error.main }} />
                </Stack>

                {topExpenseCategories.length === 0 ? (
                  <Box sx={{ py: 2.6, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">No category data yet.</Typography>
                    <PrimaryButton startIcon={<Add />} onClick={() => navigate(EXPENSES_ROUTE_PATH)} sx={{ mt: 1.2 }}>
                      Add First Expense
                    </PrimaryButton>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {topExpenseCategories.map((entry) => {
                      const ratio = totalExpenses > 0 ? (entry.amount / totalExpenses) * 100 : 0;
                      return (
                        <Box
                          key={entry.name}
                          sx={{
                            p: 1.1,
                            borderRadius: "10px",
                            border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                            backgroundColor: alpha(theme.palette.background.paper, 0.45),
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                              {entry.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatCurrency(entry.amount)} ({ratio.toFixed(0)}%)
                            </Typography>
                          </Stack>
                          <Box sx={{ height: 7, borderRadius: 999, backgroundColor: alpha(theme.palette.text.primary, 0.14), overflow: "hidden" }}>
                            <Box
                              sx={{
                                width: `${ratio}%`,
                                height: "100%",
                                background: `linear-gradient(90deg, ${theme.palette.error.dark}, ${theme.palette.error.light})`,
                              }}
                            />
                          </Box>
                        </Box>
                      );
                    })}

                    <PrimaryButton onClick={() => navigate(CATEGORIES_ROUTE_PATH)} sx={{ mt: 0.5 }}>
                      Refine Categories
                    </PrimaryButton>
                  </Stack>
                )}
              </SurfaceCard>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <SurfaceCard sx={{ p: 2.2, borderRadius: "16px", height: "100%" }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Top Income Categories</Typography>
                  <NorthEast sx={{ color: theme.palette.success.main }} />
                </Stack>

                {topIncomeCategories.length === 0 ? (
                  <Box sx={{ py: 2.6, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">No income category data yet.</Typography>
                    <PrimaryButton startIcon={<Add />} onClick={() => navigate(INCOMES_ROUTE_PATH)} sx={{ mt: 1.2 }}>
                      Add First Income
                    </PrimaryButton>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {topIncomeCategories.map((entry) => {
                      const ratio = totalIncome > 0 ? (entry.amount / totalIncome) * 100 : 0;
                      return (
                        <Box
                          key={entry.name}
                          sx={{
                            p: 1.1,
                            borderRadius: "10px",
                            border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                            backgroundColor: alpha(theme.palette.background.paper, 0.45),
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                              {entry.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatCurrency(entry.amount)} ({ratio.toFixed(0)}%)
                            </Typography>
                          </Stack>
                          <Box sx={{ height: 7, borderRadius: 999, backgroundColor: alpha(theme.palette.text.primary, 0.14), overflow: "hidden" }}>
                            <Box
                              sx={{
                                width: `${ratio}%`,
                                height: "100%",
                                background: `linear-gradient(90deg, ${theme.palette.success.dark}, ${theme.palette.success.light})`,
                              }}
                            />
                          </Box>
                        </Box>
                      );
                    })}

                    <PrimaryButton onClick={() => navigate(CATEGORIES_ROUTE_PATH)} sx={{ mt: 0.5 }}>
                      Refine Categories
                    </PrimaryButton>
                  </Stack>
                )}
              </SurfaceCard>
            </Grid>
          </Grid>

          <Grid container spacing={2.2} sx={{ mb: 2.4 }}>
            <Grid item xs={12} lg={6}>
              <SurfaceCard sx={{ p: 2.2, borderRadius: "16px" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Recent Expenses</Typography>
                  <Chip icon={<South />} label="Latest outflows" size="small" sx={{ borderRadius: "8px" }} />
                </Stack>

                {recentExpenses.length === 0 ? (
                  <Box sx={{ py: 2.6, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">No expenses yet for this month.</Typography>
                    <PrimaryButton startIcon={<Add />} onClick={() => navigate(EXPENSES_ROUTE_PATH)} sx={{ mt: 1.3 }}>
                      Add Expense
                    </PrimaryButton>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {recentExpenses.map((item) => (
                      <Box
                        key={`expense-${item.id}`}
                        sx={{
                          p: 1.15,
                          borderRadius: "10px",
                          border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                          backgroundColor: alpha(theme.palette.background.paper, 0.42),
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                              {item.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {formatDate(item.date)} • {item.category}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={`-${formatCurrency(item.amount)}`}
                            color="error"
                            variant="outlined"
                            sx={{ fontWeight: 700 }}
                          />
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}
              </SurfaceCard>
            </Grid>

            <Grid item xs={12} lg={6}>
              <SurfaceCard sx={{ p: 2.2, borderRadius: "16px" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Recent Income</Typography>
                  <Chip icon={<NorthEast />} label="Latest inflows" size="small" sx={{ borderRadius: "8px" }} />
                </Stack>

                {recentIncomes.length === 0 ? (
                  <Box sx={{ py: 2.6, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">No income yet for this month.</Typography>
                    <PrimaryButton startIcon={<Add />} onClick={() => navigate(INCOMES_ROUTE_PATH)} sx={{ mt: 1.3 }}>
                      Add Income
                    </PrimaryButton>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {recentIncomes.map((item) => (
                      <Box
                        key={`income-${item.id}`}
                        sx={{
                          p: 1.15,
                          borderRadius: "10px",
                          border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                          backgroundColor: alpha(theme.palette.background.paper, 0.42),
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                              {item.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {formatDate(item.date)} • {item.category}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={`+${formatCurrency(item.amount)}`}
                            color="success"
                            variant="outlined"
                            sx={{ fontWeight: 700 }}
                          />
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}
              </SurfaceCard>
            </Grid>
          </Grid>

          <Grid container spacing={{ xs: 1.4, md: 2 }} sx={{ mb: { xs: 2, md: 3 } }}>
            <Grid item xs={12} sm={6} md={3}>
              <SurfaceCard
                role="button"
                tabIndex={0}
                aria-label="Manage Expenses"
                sx={{
                  p: 2,
                  borderRadius: "14px",
                  cursor: "pointer",
                  '&:hover': { transform: 'translateY(-2px)' },
                  transition: 'all 0.24s ease',
                }}
                onClick={() => navigate(EXPENSES_ROUTE_PATH)}
                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') navigate(EXPENSES_ROUTE_PATH); }}
              >
                <MoneyOff sx={{ color: theme.palette.error.main, mb: 1 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
                  Manage Expenses
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add and review spending
                </Typography>
              </SurfaceCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <SurfaceCard
                role="button"
                tabIndex={0}
                aria-label="Manage Income"
                sx={{
                  p: 2,
                  borderRadius: "14px",
                  cursor: "pointer",
                  '&:hover': { transform: 'translateY(-2px)' },
                  transition: 'all 0.24s ease',
                }}
                onClick={() => navigate(INCOMES_ROUTE_PATH)}
                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') navigate(INCOMES_ROUTE_PATH); }}
              >
                <AttachMoney sx={{ color: theme.palette.success.main, mb: 1 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
                  Manage Income
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Capture every earning source
                </Typography>
              </SurfaceCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <SurfaceCard
                role="button"
                tabIndex={0}
                aria-label="View Reports"
                sx={{
                  p: 2,
                  borderRadius: "14px",
                  cursor: "pointer",
                  '&:hover': { transform: 'translateY(-2px)' },
                  transition: 'all 0.24s ease',
                }}
                onClick={() => navigate(MONTHLY_REPORTS_ROUTE_PATH)}
                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') navigate(MONTHLY_REPORTS_ROUTE_PATH); }}
              >
                <Assessment sx={{ color: theme.palette.primary.main, mb: 1 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
                  View Reports
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Deep monthly analysis
                </Typography>
              </SurfaceCard>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <SurfaceCard
                role="button"
                tabIndex={0}
                aria-label="Categories"
                sx={{
                  p: 2,
                  borderRadius: "14px",
                  cursor: "pointer",
                  '&:hover': { transform: 'translateY(-2px)' },
                  transition: 'all 0.24s ease',
                }}
                onClick={() => navigate(CATEGORIES_ROUTE_PATH)}
                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') navigate(CATEGORIES_ROUTE_PATH); }}
              >
                <Category sx={{ color: theme.palette.primary.main, mb: 1 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 700 }}>
                  Categories
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Keep tracking accurate
                </Typography>
              </SurfaceCard>
            </Grid>
          </Grid>
        </Box>
      </Fade>
    </Container>
  );
};

export default LoggedInHomePageComponent;
